<style lang="less">
  @import "../../../style/theme.less";

  .editor {
    .button-group {
      left: 50%;
      transform: translateX(-50%);
    }

    div.menububble {
        position: absolute;
        z-index: -1;
        opacity: 0;

        &.is-active {
          opacity: 1;
          z-index: 10;
          background-color: @body-background;
          border-radius: @border-radius-base;
          padding: 6px;
        }
      }

    .content {
      .ProseMirror {
        padding: 8px;
        border: 1px solid #303030;
        border-radius: @border-radius-base;
      }
    }
  }
</style>

<template>
  <div class="editor">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <a-button-group class="button-group">
        <a-button
          @click="$emit('popout')"
          icon="arrows-alt"
        />

        <a-button
          :type="isActive.bold() ? 'primary' : ''"
          @click="commands.bold"
          icon="bold"
        />

        <a-button
          :type="isActive.italic() ? 'primary' : ''"
          @click="commands.italic"
          icon="italic"
        />

        <a-button
          :type="isActive.strike() ? 'primary' : ''"
          @click="commands.strike"
          icon="strikethrough"
        />

        <a-button
          :type="isActive.underline() ? 'primary' : ''"
          @click="commands.underline"
          icon="underline"
        />

        <a-button
          :type="editor.activeMarkAttrs.aligntext.align === 'left' ? 'primary' : ''"
          @click="commands.aligntext({ align: 'left' })"
          icon="align-left"
        />

        <a-button
          :type="editor.activeMarkAttrs.aligntext.align === 'center' ? 'primary' : ''"
          @click="commands.aligntext({ align: 'center' })"
          icon="align-center"
        />

        <a-button
          :type="editor.activeMarkAttrs.aligntext.align === 'right' ? 'primary' : ''"
          @click="commands.aligntext({ align: 'right' })"
          icon="align-right"
        />

        <a-button
          :type="isActive.heading({ level: 1 }) ? 'primary' : ''"
          @click="commands.heading({ level: 1 })"
        >
          H1
        </a-button>

        <a-button
          :type="isActive.heading({ level: 2 }) ? 'primary' : ''"
          @click="commands.heading({ level: 2 })"
        >
          H2
        </a-button>

        <a-button
          :type="isActive.heading({ level: 3 }) ? 'primary' : ''"
          @click="commands.heading({ level: 3 })"
        >
          H3
        </a-button>

        <a-button
          :type="isActive.bullet_list() ? 'primary' : ''"
          @click="commands.bullet_list"
          icon="unordered-list"
        />

        <a-button
          :type="isActive.ordered_list() ? 'primary' : ''"
          @click="commands.ordered_list"
          icon="ordered-list"
        />

        <a-button
          @click="commands.horizontal_rule"
          icon="line"
        />

        <a-button
          @click="commands.undo"
          icon="undo"
        />

        <a-button
          @click="commands.redo"
          icon="redo"
        />

        <a-button
          type="primary"
          @click="() => $emit('save', getHTML())"
        >
          <a-icon type="save" />
          Save
        </a-button>

      </a-button-group>
    </editor-menu-bar>

    <editor-menu-bubble :editor="editor" v-slot="{ commands, isActive, menu }">
      <div
        class="menububble"
        :class="{ 'is-active': menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
      >
        <a-button-group>

          <a-button
            :type="isActive.bold() ? 'primary' : ''"
            @click="commands.bold"
            icon="bold"
          />

          <a-button
            :type="isActive.italic() ? 'primary' : ''"
            @click="commands.italic"
            icon="italic"
          />

          <a-button
            :type="isActive.strike() ? 'primary' : ''"
            @click="commands.strike"
            icon="strikethrough"
          />

          <a-button
            :type="isActive.underline() ? 'primary' : ''"
            @click="commands.underline"
            icon="underline"
          />

          <a-button
            :type="editor.activeMarkAttrs.aligntext.align === 'left' ? 'primary' : ''"
            @click="commands.aligntext({ align: 'left' })"
            icon="align-left"
          />

          <a-button
            :type="editor.activeMarkAttrs.aligntext.align === 'center' ? 'primary' : ''"
            @click="commands.aligntext({ align: 'center' })"
            icon="align-center"
          />

          <a-button
            :type="editor.activeMarkAttrs.aligntext.align === 'right' ? 'primary' : ''"
            @click="commands.aligntext({ align: 'right' })"
            icon="align-right"
          />

          <a-button
            :type="isActive.heading({ level: 1 }) ? 'primary' : ''"
            @click="commands.heading({ level: 1 })"
          >
            H1
          </a-button>

          <a-button
            :type="isActive.heading({ level: 2 }) ? 'primary' : ''"
            @click="commands.heading({ level: 2 })"
          >
            H2
          </a-button>

          <a-button
            :type="isActive.heading({ level: 3 }) ? 'primary' : ''"
            @click="commands.heading({ level: 3 })"
          >
            H3
          </a-button>

          <a-button
            :type="isActive.bullet_list() ? 'primary' : ''"
            @click="commands.bullet_list"
            icon="unordered-list"
          />

          <a-button
            :type="isActive.ordered_list() ? 'primary' : ''"
            @click="commands.ordered_list"
            icon="ordered-list"
          />

          <a-button
            @click="commands.horizontal_rule"
            icon="line"
          />

        </a-button-group>
      </div>
    </editor-menu-bubble>

    <div class="spacer-lg" />

    <editor-content class="content" :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from 'tiptap';
import {
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  Bold,
  Italic,
  Link,
  Strike,
  Underline,
  History
} from 'tiptap-extensions';

import plugins from './plugins/';

export default {
  name: 'editor',

  data: () => ({
    editor: new Editor({
      extensions: [
        new BulletList(),
        new Heading({ levels: [1, 2, 3,], }),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new Link(),
        new Bold(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new plugins.AlignText(),
      ],
    }),
  }),

  props: ['content',],

  components: {
    EditorContent,
    EditorMenuBar,
    EditorMenuBubble,
  },

  mounted() {
    if (this.content) {
      this.editor.setContent(this.content);
    }
  },

  methods: {
    setContent(content) {
      if (content && this.editor) {
        this.editor.setContent(content);
      }
    },

    getHTML() {
      return this.editor.getHTML();
    },
  },

  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>
